import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import { useDashboard } from '../../hooks/useDashboard';
import { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import { useDebounce } from 'use-debounce';
import ReactGridLayout, { useContainerWidth } from 'react-grid-layout';
import { themeToCssVars } from '@/lib/dashboardTheme';
import {
  applyDefaultValueChanges,
  collectDefaultValueChanges,
  type DefaultValueChange,
} from '@/utils/dashboardDefaultChanges';
import type { Layout, LayoutItem } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Loader2, RefreshCw, RotateCcw, Settings, Plus, Save, GripVertical, Trash2, AlertCircle, ChevronDown } from 'lucide-react';
import { Chart } from '../../components/Chart';
import { EditableTitle } from '../../components/EditableTitle';
import type { DashboardComponent, DashboardFilterOption } from '../../services/dashboard.service';
import { AddDashboardFilter } from '../../components/dashboard/AddDashboardFilter';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { SlidersHorizontal, Palette } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ThemeEditor } from '@/components/dashboard/ThemeEditor';
import { Switch } from '@/components/ui/switch';
import { useCompactSidebar } from '@/components/chat/useCompactSidebar';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelOverrides';
import { resolveInheritedColors } from '@/lib/dashboardTheme';
import type { DashboardTheme } from '@/services/dashboard.service';
import { dashboardService } from '../../services/dashboard.service';
import { MarkdownText } from '../../components/chat/MarkdownText';
import { Button } from '../../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { analysisService, type Analysis } from '../../services/analysis.service';
import { useAnalysis } from '../../hooks/useAnalysis';
import { toastService } from '../../services/toast.service';
import { Combobox } from '../../components/ui/combobox';
import { MultiSelectCombobox } from '../../components/ui/multi-select-combobox';
import { useUniqueValuesInDimension } from '../../services/reportBuilder.service';
import type { AnalysisDimensionFilter } from '../../utils/filterComparators';
import { useIsInDraftMode } from '../../hooks/useDraftMode';
import { useDimensionById } from '../../hooks/useDimension';
import { useIsMobile } from '../../hooks/use-mobile';
import { SidebarTrigger } from '../../components/ui/sidebar';
import { useWhitelabelValue } from '../../hooks/useWhitelabelOverrides';
import { ComponentConfigSheet } from '../../components/ComponentConfigSheet';
import { useImmer } from 'use-immer';
import { resolveComponentTitle, resolveComponentDescription } from '../../utils/dashboardComponentResolvers';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

import { DateRangePicker, DATE_RANGE_OPTIONS, mapDateRangeFromBackend, mapDateRangeToBackend } from '../../components/ui/date-range-picker';
import { useAvailableDashboardDateRangeOptions } from '../../hooks/useAvailableDashboardDateRangeOptions';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { dateDimensionCustomPeriodService } from '../../services/dateDimensionCustomPeriod.service';
import { useFeatureFlag, useFeatureFlagResolved } from '../../services/featureFlag.service';
import { FeatureFlag } from '../../services/featureFlag.service';
import { useTenantConfig } from '../../hooks/useTenantConfig';
import {
  buildPersistenceKey,
  diffFromDefaults,
  mergePersistedOverDefaults,
  readPersistedFromStorage,
  reconcilePersistedFilters,
  writePersistedToStorage,
  type DashboardDefaults,
} from '../../utils/dashboardFilterPersistence';

import { simplePlural } from '../../utils/pluralize';

interface DashboardComponentRendererProps {
  component: DashboardComponent;
  dateRangeConfig?: any;
  dimensionFilters?: AnalysisDimensionFilter[];
  pendingDimensionFilters?: AnalysisDimensionFilter[];
  forceShowPrepLoader?: boolean;
  onFilterChange?: (componentId: string, filter: AnalysisDimensionFilter | null) => void;
  currentFilter?: AnalysisDimensionFilter | null;
  disabled?: boolean;
  onApplyFilters?: () => void;
  filterApplyDisabled?: boolean;
  onChartRefresh?: () => void;
  onChartRefreshFinished?: () => void;
  chartRefreshDisabled?: boolean;
  isEditing?: boolean;
  onConfigure?: (component: DashboardComponent) => void;
  componentError?: string;
}

// Narrowest the grid is allowed to get before the content area scrolls
// sideways instead. Roughly 60px per column, where a chart still reads.
const MIN_GRID_WIDTH = 720;

interface FilterComponentProps {
  // 'panel' is the tile form, with its own Apply and reset. 'bar' is the
  // dashboard's filter bar, where one shared Refresh applies every filter at
  // once — an Apply button per control was the single busiest thing on the page.
  variant?: 'panel' | 'bar';
  filterConfig: any; // DashboardFilterOption
  componentId: string;
  onFilterChange?: (componentId: string, filter: AnalysisDimensionFilter | null) => void;
  currentFilter?: AnalysisDimensionFilter | null;
  activeFilter?: AnalysisDimensionFilter | null;
  otherActiveFilters?: AnalysisDimensionFilter[];
  disabled?: boolean;
  onApply?: () => void;
  applyDisabled?: boolean;
}

function FilterComponent({
  filterConfig,
  componentId,
  onFilterChange,
  currentFilter,
  activeFilter,
  otherActiveFilters,
  disabled = false,
  onApply,
  applyDisabled = false,
  variant = 'panel',
}: FilterComponentProps) {
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery] = useDebounce(query, 500);

  // Only support DIMENSION type filters
  if (filterConfig?.filter_type !== 'DIMENSION' || !filterConfig?.dimension_id) {
    return (
      <div className="p-4 h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <p className="text-sm">Unsupported filter type</p>
          <p className="text-xs mt-2">Only DIMENSION filters are supported</p>
        </div>
      </div>
    );
  }

  const { data: dimensionValues, isLoading } = useUniqueValuesInDimension(
    filterConfig.dimension_id,
    debouncedQuery,
    otherActiveFilters && otherActiveFilters.length > 0 ? otherActiveFilters : undefined,
  );

  const { data: dimension } = useDimensionById(filterConfig.dimension_id);

  const getFilterValues = useCallback((filter?: AnalysisDimensionFilter | null) => {
    if (filter?.value) {
      if (Array.isArray(filter.value)) {
        return filter.value;
      }
      return [filter.value];
    }
    return [];
  }, []);

  const selectedValues = useMemo(() => getFilterValues(currentFilter), [currentFilter, getFilterValues]);

  const valueOptions = useMemo(() => {
    const dbValues = dimensionValues ?? [];
    const dbSet = new Set(dbValues);
    // Always include currently selected values so the user can see and clear them,
    // even if they've been filtered out by other active filters
    const extra = selectedValues.filter(v => !dbSet.has(v));
    const allValues = [...dbValues, ...extra];
    return allValues.sort((a, b) => a.localeCompare(b)).map(v => ({
      value: v,
      label: v.length > 50 ? `${v.slice(0, 50)}...` : v,
    }));
  }, [dimensionValues, selectedValues]);

  const allowMultiple = filterConfig.allow_multiple ?? false;

  const placeholder = useMemo(() => {
    if (dimensionValues?.length === 1) return dimensionValues[0];
    if (dimension?.name) return `All ${simplePlural(dimension.name)}`;
    return 'All values';
  }, [dimensionValues, dimension?.name]);

  const currentValues = useMemo(() => getFilterValues(currentFilter), [currentFilter, getFilterValues]);
  const activeValues = useMemo(() => getFilterValues(activeFilter), [activeFilter, getFilterValues]);
  const hasPendingChanges = useMemo(
    () => JSON.stringify(currentValues) !== JSON.stringify(activeValues),
    [currentValues, activeValues]
  );

  const defaultValues = useMemo(() => {
    const dv = filterConfig.default_value;
    if (dv == null) return [] as string[];
    return Array.isArray(dv) ? dv : [dv];
  }, [filterConfig.default_value]);

  const differsFromDefault = useMemo(() => {
    const cur = [...currentValues].sort();
    const def = [...defaultValues].sort();
    return JSON.stringify(cur) !== JSON.stringify(def);
  }, [currentValues, defaultValues]);

  // Show the reset link when there's somewhere meaningful to reset *to*:
  // - a saved default exists, OR
  // - the user has previously applied a value (activeValues non-empty ⇒
  //   it's in the per-tab session cache). The "Clear selection" variant
  //   only ever appears when the user has actually committed a value,
  //   not while they're mid-typing a fresh selection.
  const showResetLink = useMemo(
    () => differsFromDefault && (defaultValues.length > 0 || activeValues.length > 0),
    [differsFromDefault, defaultValues, activeValues],
  );

  const handleResetToDefault = () => {
    if (defaultValues.length === 0) {
      onFilterChange?.(componentId, null);
      return;
    }
    onFilterChange?.(componentId, {
      dimension_id: filterConfig.dimension_id,
      comparator: 'IN',
      value: defaultValues,
    });
  };

  const handleValueChange = (values: string | string[]) => {
    // Normalize: single-select Combobox clears via onValueChange(''); treat the
    // empty string as "cleared" rather than as a real value.
    const filterValue = Array.isArray(values)
      ? values.filter((v) => v !== '')
      : values
        ? [values]
        : [];

    if (filterValue.length === 0) {
      onFilterChange?.(componentId, null);
      return;
    }

    const filter: AnalysisDimensionFilter = {
      dimension_id: filterConfig.dimension_id,
      comparator: 'IN',
      value: filterValue,
    };

    onFilterChange?.(componentId, filter);
  };

  const isBar = variant === 'bar';

  return (
    <div
      className={
        isBar
          ? 'flex flex-col gap-1 w-[240px] min-w-[130px]'
          : 'px-4 h-full flex flex-col justify-center'
      }
    >
      {filterConfig.description && !isBar && (
        <p className="text-sm text-muted-foreground mb-2">{filterConfig.description}</p>
      )}
      {filterConfig.input_label && (
        <label
          className={`text-xs font-medium text-muted-foreground ${
            isBar ? 'truncate' : 'mb-1.5'
          }`}
        >
          {filterConfig.input_label}
        </label>
      )}
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          {allowMultiple ? (
            <MultiSelectCombobox
              options={valueOptions}
              value={currentValues}
              onValueChange={(values) => handleValueChange(values)}
              placeholder={placeholder}
              emptyText="No values available."
              onInputChange={setQuery}
              isLoading={isLoading}
              disabled={disabled}
              className={isBar ? 'w-full min-h-10' : 'w-full'}
            />
          ) : (
            <Combobox
              options={valueOptions}
              value={currentValues.length > 0 ? currentValues[0] : undefined}
              onValueChange={(value) => handleValueChange(value ?? '')}
              placeholder={placeholder}
              emptyText="No values available."
              disabled={disabled}
              className="w-full"
            />
          )}
        </div>
        {!isBar && (
          <Button
            size="sm"
            onClick={() => onApply?.()}
            disabled={disabled || applyDisabled || !hasPendingChanges}
          >
            Apply
          </Button>
        )}
      </div>
      {!isBar && (
      <button
        type="button"
        onClick={handleResetToDefault}
        disabled={disabled || !showResetLink}
        aria-hidden={!showResetLink}
        tabIndex={showResetLink ? 0 : -1}
        title={
          defaultValues.length > 0
            ? `Reset to default: ${defaultValues.join(', ')}`
            : 'Clear selection'
        }
        className={`mt-2 self-start inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:cursor-not-allowed max-w-full transition-opacity ${
          showResetLink ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <RotateCcw className="h-3 w-3 flex-shrink-0" />
        <span className="truncate">
          {defaultValues.length === 0
            ? 'Clear selection'
            : defaultValues.length === 1
              ? `Reset to default: ${defaultValues[0]}`
              : `Reset to default (${defaultValues.length} values)`}
        </span>
      </button>
      )}
    </div>
  );
}

// Memoized because opening the component settings sheet sets state on the
// dashboard, and without this every panel re-rendered to show a side sheet —
// measured at a single 3.2s synchronous task on a twelve-panel dashboard, of
// which 134ms was network. Filter, date-range and resize changes went through
// the same path. The props below are all either values or stable identities
// (useState arrays, useCallback handlers), so the default shallow compare is
// enough; adding an inline arrow or a freshly built array at a call site
// silently undoes this.
const DashboardComponentRenderer = memo(function DashboardComponentRenderer({
  component,
  dateRangeConfig,
  dimensionFilters,
  pendingDimensionFilters,
  forceShowPrepLoader = false,
  onFilterChange,
  currentFilter,
  disabled = false,
  onApplyFilters,
  filterApplyDisabled = false,
  onChartRefresh,
  onChartRefreshFinished,
  chartRefreshDisabled = false,
  isEditing = false,
  onConfigure,
  componentError,
}: DashboardComponentRendererProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Fetch linked object data for title/description fallback (cached, no extra API calls)
  const { data: linkedAnalysis, isLoading: analysisLoading } = useAnalysis(
    component.object_type === 'ANALYSIS' ? component.object_id || null : null,
    true,
  );
  const { data: linkedDimension, isLoading: dimensionLoading } = useDimensionById(
    component.object_type === 'FILTER' ? component.filter_config?.dimension_id || '' : '',
    { enabled: component.object_type === 'FILTER' && !!component.filter_config?.dimension_id },
  );

  // Resolve title/description: component override → linked object → fallback
  const linkedObject = component.object_type === 'ANALYSIS' ? linkedAnalysis : linkedDimension;
  const linkedObjectLoading = component.object_type === 'ANALYSIS' ? analysisLoading : dimensionLoading;
  const resolvedTitle = useMemo(() => {
    if (component.title) return component.title;
    if (linkedObjectLoading) return '';
    return resolveComponentTitle(component, linkedObject);
  }, [component.title, component.object_type, linkedObject?.name, linkedObjectLoading]);
  const resolvedDescription = useMemo(
    () => resolveComponentDescription(component, linkedObject),
    [component.description, component.object_type, linkedObject?.description],
  );

  const renderContent = () => {
    if (componentError) {
      return (
        <div className="p-4 h-full flex items-center justify-center">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md w-full">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-destructive text-sm font-medium">Build failed</p>
                <p className="text-destructive/80 text-xs mt-1">{componentError}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (component.object_type === 'ANALYSIS' && component.object_id) {
      // Map date range from backend format to frontend format
      const mappedDateRange = component.ignore_date_range 
        ? undefined 
        : mapDateRangeFromBackend(dateRangeConfig?.date_range);
      
      // Render chart component with auto-prep disabled (dashboard handles prep centrally)
      return (
        <Chart
          analysisId={component.object_id}
          componentId={component.id}
          dateRange={mappedDateRange}
          customDateStart={component.ignore_date_range ? undefined : dateRangeConfig?.custom_date_range_start}
          customDateEnd={component.ignore_date_range ? undefined : dateRangeConfig?.custom_date_range_end}
          dimensionFilters={dimensionFilters}
          disableAutoPrep={true}
          compact={true}
          forceShowPrepLoader={forceShowPrepLoader}
          onRefresh={onChartRefresh}
          onRefreshFinished={onChartRefreshFinished}
          refreshDisabled={chartRefreshDisabled}
          hideRefreshButton={true}
          disableSave={true}
          disableAddToDashboard={true}
          enableExploreFromHere={true}
          disableSort={component.disable_sort}
        />
      );
    } else if (component.object_type === 'INLINE_ANALYSIS' && component.analysis_definition) {
      // The definition is carried on the component, so there is no document to
      // fetch: Chart takes it as `workingAnalysis` and useAnalysis no-ops on the
      // empty id. Everything downstream — prep, superset reuse, row-level
      // security — is identical to a saved analysis.
      const mappedDateRange = component.ignore_date_range
        ? undefined
        : mapDateRangeFromBackend(dateRangeConfig?.date_range);

      return (
        <Chart
          analysisId=""
          workingAnalysis={component.analysis_definition as any}
          componentId={component.id}
          dateRange={mappedDateRange}
          customDateStart={component.ignore_date_range ? undefined : dateRangeConfig?.custom_date_range_start}
          customDateEnd={component.ignore_date_range ? undefined : dateRangeConfig?.custom_date_range_end}
          dimensionFilters={dimensionFilters}
          disableAutoPrep={true}
          compact={true}
          forceShowPrepLoader={forceShowPrepLoader}
          onRefresh={onChartRefresh}
          onRefreshFinished={onChartRefreshFinished}
          refreshDisabled={chartRefreshDisabled}
          hideRefreshButton={true}
          disableSave={true}
          disableAddToDashboard={true}
          disableSort={component.disable_sort}
        />
      );
    } else if (component.object_type === 'MARKDOWN' && component.markdown_content) {
      // Placeholder for markdown - will implement later
      return (
        <div className="p-4 h-full overflow-auto">
          <MarkdownText content={component.markdown_content} />
        </div>
      );
    } else if (component.object_type === 'FILTER' && component.filter_config) {
      return (
        <FilterComponent
          filterConfig={component.filter_config}
          componentId={component.id}
          onFilterChange={onFilterChange}
          currentFilter={currentFilter}
          activeFilter={dimensionFilters?.find((filter) => filter.dimension_id === component.filter_config?.dimension_id) || null}
          otherActiveFilters={pendingDimensionFilters?.filter((filter) => filter.dimension_id !== component.filter_config?.dimension_id)}
          disabled={disabled}
          onApply={onApplyFilters}
          applyDisabled={filterApplyDisabled}
        />
      );
    } else {
      // Placeholder for other types
      return (
        <div className="p-4 h-full flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <p className="text-sm">Component type: {component.object_type || 'Unknown'}</p>
            <p className="text-xs mt-2">Implementation coming soon</p>
          </div>
        </div>
      );
    }
  };

  const showHeader = !component.hide_header;

  // Card header bg: "transparent" = no bg/border, hex = custom, "" = default theme
  const cardHeaderBg = useWhitelabelValue('card_header_bg_color', '');
  const cardTitleColor = useWhitelabelValue('card_title_color', '');
  const isTransparentHeader = cardHeaderBg === 'transparent';
  const headerStyle = cardHeaderBg && !isTransparentHeader ? { backgroundColor: cardHeaderBg + '80' } : undefined;
  // The default header has no fill and no divider: the card already has a
  // border, and a filled band inside a bordered box reads as two nested boxes.
  // Space does the separating instead, which is why the padding is asymmetric —
  // more above the title than below it, so the heading sits with the content it
  // labels rather than floating between the border and the chart.
  //
  // A tenant-set header colour keeps its divider, since a colour band needs a
  // defined bottom edge to look deliberate rather than like a rendering seam.
  const headerClassName = [
    'pl-panel-header px-4 pt-3 pb-2 flex-shrink-0 flex items-start justify-between gap-2',
    headerStyle && !isTransparentHeader ? 'border-b border-border' : '',
  ].join(' ').trim();

  // A filter is a control, not a panel. Giving it the same bordered card as a
  // chart makes a row of dropdowns read as three empty charts, which is most of
  // what makes the top of a dashboard look cluttered.
  const isControl = component.object_type === 'FILTER';

  // bg-card, not bg-background: the panel is the card surface, which is the
  // token the theme (and the tenant's whitelabeling before it) actually sets.
  // The pl-* classes carry no styling — they are stable hooks so colors beyond
  // this fixed set can be reached later without re-deriving the markup.
  const panelClassName = [
    'group/card pl-panel w-full h-full flex flex-col overflow-hidden relative',
    `pl-panel--${(component.object_type || 'unknown').toLowerCase()}`,
    isControl ? '' : 'bg-card border border-border rounded-sm',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={panelClassName}
      style={themeToCssVars(component.theme, 'component')}
      data-component-id={component.id}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Component Header - only shown if hide_header is false */}
      {showHeader && (
        <div className={headerClassName} style={headerStyle}>
          <div className="min-w-0 flex-1">
            <h3 className={`pl-panel-title text-sm font-medium leading-tight truncate ${cardTitleColor ? '' : 'text-foreground'}`} style={cardTitleColor ? { color: cardTitleColor } : undefined}>{resolvedTitle}</h3>
            {resolvedDescription && (
              <p className="pl-panel-description text-xs text-muted-foreground break-words mt-0.5" title={resolvedDescription}>{resolvedDescription}</p>
            )}
          </div>
          {/* Edit affordances follow the chart's actions: present on the panel you
              are pointing at, absent on the nine you are not. You have to be over
              a panel to drag or configure it anyway, so nothing is lost, and the
              page stops looking like a form. */}
          {isEditing && (
            <div className="flex items-center gap-1 opacity-0 group-hover/card:opacity-100 focus-within:opacity-100 transition-opacity">
              <div className="drag cursor-move p-1 hover:bg-muted rounded-sm">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
              {onConfigure && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 nodrag"
                  onClick={() => onConfigure(component)}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Floating controls when header is hidden - only visible on hover */}
      {!showHeader && isEditing && isHovered && (
        <div className="absolute top-2 right-2 flex items-center gap-1 z-10 bg-background/80 backdrop-blur-sm rounded-sm border border-border p-1">
          <div className="drag cursor-move p-1 hover:bg-muted rounded-sm">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          {onConfigure && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 nodrag"
              onClick={() => onConfigure(component)}
            >
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {/* Component Content */}
      <div className="pl-panel-body flex-1 min-h-0 overflow-hidden nodrag">
        {renderContent()}
      </div>
    </div>
  );
});

interface DashboardDetailProps {
  embeddedDashboardId?: string;
  // Size to the parent instead of the viewport. A dashboard in a side panel sits
  // below that panel's header, so calc(100vh) overflows by exactly that height.
  fillParent?: boolean;
  // Render without the Pliable app shell. Set by the chromeless view route;
  // the dashboard's own presentation.chromeless can also turn it on. Purely
  // presentational — every auth gate still applies (see AuthenticatedRootPage).
  chromeless?: boolean;
}

// Outer gate — calls only the flag hook. Once the flag resolves, mounts
// DashboardDetailContent (which has the rest of the hooks). Splitting like
// this keeps hook order stable across renders: while loading, only the flag
// hook runs; after loading, the inner component is mounted with its own
// hook list.
export default function DashboardDetail({ embeddedDashboardId, chromeless, fillParent }: DashboardDetailProps = {}) {
  const { value: isAnalysisSectionEnabled, isLoading: isFlagLoading } =
    useFeatureFlagResolved(FeatureFlag.ACCESS_ANALYSIS_SECTION, false);

  // When accessed via route (not embedded), gate on the feature flag.
  // Wait for the flag to resolve before redirecting so a cold direct-URL
  // load doesn't bounce users home before LaunchDarkly has reported in.
  if (!embeddedDashboardId) {
    if (isFlagLoading) {
      return (
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (!isAnalysisSectionEnabled) {
      return <Navigate to="/" replace />;
    }
  }

  return <DashboardDetailContent embeddedDashboardId={embeddedDashboardId} chromeless={chromeless} fillParent={fillParent} />;
}

function DashboardDetailContent({ embeddedDashboardId, chromeless, fillParent }: DashboardDetailProps = {}) {
  const { dashboardId: routeDashboardId } = useParams<{ dashboardId: string }>();
  const dashboardId = embeddedDashboardId || routeDashboardId;
  const { data: dashboard, isLoading, error } = useDashboard(dashboardId || null);
  const { data: tenantConfig } = useTenantConfig();
  const supersetsEnabled = !!tenantConfig?.dashboard_supersets_enabled;
  // Presentation is about framing only — never about access. A chromeless
  // dashboard is still behind every gate AuthenticatedRootPage applies.
  const presentation = dashboard?.presentation;
  const isChromeless = !!chromeless || !!embeddedDashboardId || !!presentation?.chromeless;
  const hidePageHeader = !!presentation?.hide_page_header;
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isChartRefreshing, setIsChartRefreshing] = useState(false); // Track if any chart is refreshing
  const [componentErrors, setComponentErrors] = useState<Map<string, string>>(new Map());
  const { width: containerWidth, containerRef, mounted: containerMounted, measureWidth } = useContainerWidth();
  const isInDraftMode = useIsInDraftMode();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { data: customPeriodVariants } = useQuery({
    queryKey: ['customPeriodVariants'],
    queryFn: () => dateDimensionCustomPeriodService.getVariants(),
    staleTime: 5 * 60 * 1000,
  });
  const hasFiscalPeriods = (customPeriodVariants?.length ?? 0) > 0;
  const availableDateRangeOptions = useAvailableDashboardDateRangeOptions(hasFiscalPeriods);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteDashboard = useCallback(async () => {
    if (!dashboardId) return;
    setIsDeleting(true);
    try {
      await dashboardService.delete(dashboardId);
      await queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      toastService.success('Dashboard deleted successfully');
      navigate('/analytics');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [dashboardId, queryClient, navigate]);

  // Debug logging for container width issues
  useEffect(() => {
    console.log('[DashboardDetail] containerWidth:', containerWidth, 'containerMounted:', containerMounted);
  }, [containerWidth, containerMounted]);

  // Force measure width when dashboard loads (the ref element wasn't available during initial mount)
  useEffect(() => {
    if (dashboard && !isLoading) {
      console.log('[DashboardDetail] Dashboard loaded, calling measureWidth');
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        measureWidth();
      });
    }
  }, [dashboard, isLoading, measureWidth]);

  // Editing state
  const [editingComponents, setEditingComponents] = useImmer<DashboardComponent[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editingFilters, setEditingFilters] = useState<DashboardFilterOption[]>([]);
  const [editingTheme, setEditingTheme] = useState<DashboardTheme | null>(null);
  const [editingCompactSidebar, setEditingCompactSidebar] = useState(false);
  const [themeSheetOpen, setThemeSheetOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Config sheet state
  const [configSheetOpen, setConfigSheetOpen] = useState(false);
  const [configuringComponent, setConfiguringComponent] = useState<DashboardComponent | null>(null);

  // Save confirmation dialog state
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [saveChangeChecked, setSaveChangeChecked] = useState<Map<string, boolean>>(new Map());

  // Read without widening the dependency list below — including this would
  // re-run the effect the moment an edit marks the dashboard dirty.
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  // Seed the editing copy from the server, and re-seed when the server's copy
  // actually changes.
  //
  // This used to key on dashboard?.id alone, which meant a refetch of the same
  // dashboard never reached the grid: in draft mode displayComponents renders
  // editingComponents, so Ellie rewriting a dashboard open in the side panel
  // refetched new components that were then ignored. last_updated is the
  // server's version marker (MongoModel.save stamps it), so it changes exactly
  // when there is something new to adopt.
  //
  // Never over unsaved local edits, which is why the original guard existed:
  // a refetch on window focus must not discard a panel the user just dragged.
  // If they have pending changes the grid stays on their version until they
  // save or discard.
  useEffect(() => {
    if (!dashboard?.components || !isInDraftMode) return;
    if (hasUnsavedChangesRef.current) return;
    setEditingComponents(dashboard.components);
    setEditingFilters(dashboard.filters || []);
    setEditingTheme(dashboard.theme || null);
    setEditingCompactSidebar(!!dashboard.presentation?.compact_sidebar);
    setHasUnsavedChanges(false);
  }, [dashboard?.id, dashboard?.last_updated, isInDraftMode]);

  // Get the components to display (editing or original)
  const displayComponents = isInDraftMode ? editingComponents : (dashboard?.components || []);
  const displayFilters = isInDraftMode ? editingFilters : (dashboard?.filters || []);
  // Draft edits paint live, so a color change is visible while it is still unsaved.
  const displayTheme = isInDraftMode ? editingTheme : (dashboard?.theme || null);
  const displayCompactSidebar = isInDraftMode
    ? editingCompactSidebar
    : !!presentation?.compact_sidebar;
  // Only the full page manages the sidebar. Embedded in the Ellie side panel,
  // SidePanelLayout is already collapsing it for its own reasons, and two
  // hooks taking turns restoring it would fight over what to restore to.
  // Chromeless has no sidebar at all, which the hook itself tolerates.
  useCompactSidebar(displayCompactSidebar && !fillParent && !isChromeless);
  const whitelabel = useWhitelabelConfig();
  // A dashboard inherits from the tenant's branding and nothing else.
  const inheritedDashboardColors = useMemo(
    () => resolveInheritedColors(whitelabel),
    [whitelabel],
  );

  // Past a handful the bar stops being a bar. The rest go behind one button
  // rather than wrapping into a second and third row of controls.
  const MAX_VISIBLE_BAR_FILTERS = 3;
  const visibleBarFilters = useMemo(
    () => displayFilters.slice(0, MAX_VISIBLE_BAR_FILTERS),
    [displayFilters],
  );
  const overflowBarFilters = useMemo(
    () => displayFilters.slice(MAX_VISIBLE_BAR_FILTERS),
    [displayFilters],
  );

  const handleAddBarFilter = useCallback((filter: DashboardFilterOption) => {
    setEditingFilters((prev) => [...prev, filter]);
    setHasUnsavedChanges(true);
  }, []);

  const mobileComponents = useMemo(() => {
    return [...displayComponents].sort((a, b) => a.y - b.y || a.x - b.x);
  }, [displayComponents]);
  
  // Pending date range state (what user is selecting, not yet applied)
  const [pendingDateRange, setPendingDateRange] = useState<string>('all_time');
  const [pendingCustomDateStart, setPendingCustomDateStart] = useState<string>('');
  const [pendingCustomDateEnd, setPendingCustomDateEnd] = useState<string>('');

  
  // Active date range config (what's currently passed to components)
  const [activeDateRangeConfig, setActiveDateRangeConfig] = useState<any>(undefined);
  
  // Active dimension filters (what's currently passed to components)
  const [dimensionFilters, setDimensionFilters] = useState<AnalysisDimensionFilter[]>([]);
  
  // Pending dimension filters (what user is selecting, not yet applied)
  const [pendingDimensionFilters, setPendingDimensionFilters] = useState<AnalysisDimensionFilter[]>([]);
  
  // Session-storage key for filter persistence (null in embed mode or before tenant loads)
  const filterStorageKey = useMemo(
    () =>
      embeddedDashboardId
        ? null
        : buildPersistenceKey(tenantConfig?.tenant_uuid, dashboardId || undefined),
    [embeddedDashboardId, tenantConfig?.tenant_uuid, dashboardId],
  );

  // Dashboard's saved defaults — used both as the merge base for persistence and
  // as the comparison source for the "Reset" affordance.
  const dashboardDefaults = useMemo<DashboardDefaults>(() => {
    const drc = dashboard?.date_range_config;
    const mappedRange = drc ? mapDateRangeFromBackend(drc.date_range) : null;
    const dimensionFilters: AnalysisDimensionFilter[] = [];
    const addDefault = (fc?: DashboardFilterOption | null) => {
      if (!fc || fc.filter_type !== 'DIMENSION' || !fc.dimension_id || !fc.default_value) return;
      const values = Array.isArray(fc.default_value) ? fc.default_value : [fc.default_value];
      if (values.length === 0) return;
      dimensionFilters.push({ dimension_id: fc.dimension_id, comparator: 'IN', value: values });
    };
    // Both kinds. Only the FILTER components were read here, so a default set
    // on a dashboard-level filter did nothing for the viewer — while the
    // backend's own _dashboard_default_filters honoured it, which is how Ellie
    // and the person looking at the dashboard ended up on different numbers.
    (dashboard?.components || []).forEach((component) => {
      if (component.object_type === 'FILTER') addDefault(component.filter_config);
    });
    (dashboard?.filters || []).forEach(addDefault);
    return {
      dateRange: mappedRange || 'all_time',
      customStart: drc?.custom_date_range_start || '',
      customEnd: drc?.custom_date_range_end || '',
      dimensionFilters,
    };
  }, [dashboard?.id]);

  const buildDateRangeConfig = useCallback(
    (dr: { dateRange: string; customStart: string; customEnd: string }) => ({
      date_range: mapDateRangeToBackend(dr.dateRange),
      custom_date_range_start: dr.dateRange === 'custom' ? dr.customStart : undefined,
      custom_date_range_end: dr.dateRange === 'custom' ? dr.customEnd : undefined,
      user_timezone: dashboard?.date_range_config?.user_timezone || 'UTC',
      previous_period: dashboard?.date_range_config?.previous_period || false,
    }),
    [dashboard?.date_range_config?.user_timezone, dashboard?.date_range_config?.previous_period],
  );

  const applyMergedFilters = useCallback(
    (merged: DashboardDefaults) => {
      setPendingDateRange(merged.dateRange);
      setPendingCustomDateStart(merged.customStart);
      setPendingCustomDateEnd(merged.customEnd);
      setPendingDimensionFilters(merged.dimensionFilters);
      setDimensionFilters(merged.dimensionFilters);
      setActiveDateRangeConfig(buildDateRangeConfig(merged));
    },
    [buildDateRangeConfig],
  );

  // Track which dashboard has been initialized so persisted writes don't re-trigger init
  const initializedDashboardIdRef = useRef<string | null>(null);

  // Single init effect: merge persisted overrides over dashboard defaults, then seed
  // BOTH pending and active state so charts fire one query, not two.
  useEffect(() => {
    if (!dashboard) return;
    // If persistence is enabled, wait for the storage key to resolve (tenantConfig load).
    if (!embeddedDashboardId && !filterStorageKey) return;
    if (initializedDashboardIdRef.current === dashboard.id) return;

    const persisted = readPersistedFromStorage(filterStorageKey);
    const reconciled = reconcilePersistedFilters(
      persisted,
      dashboard,
      availableDateRangeOptions.map((o) => o.value),
    );
    const merged = mergePersistedOverDefaults(reconciled, dashboardDefaults);
    applyMergedFilters(merged);

    // If reconcile pruned anything, write the cleaned blob back.
    if (filterStorageKey && JSON.stringify(reconciled) !== JSON.stringify(persisted)) {
      writePersistedToStorage(filterStorageKey, reconciled);
    }
    initializedDashboardIdRef.current = dashboard.id;
  }, [
    dashboard,
    embeddedDashboardId,
    filterStorageKey,
    dashboardDefaults,
    availableDateRangeOptions,
    applyMergedFilters,
  ]);

  // Persist the just-applied filter state as a diff vs dashboard defaults.
  // Called from handleRefresh at the same moment we promote pending → active,
  // so unapplied pending changes don't leak into sessionStorage.
  const persistAppliedFilters = useCallback(
    (applied: {
      dateRange: string;
      customStart: string;
      customEnd: string;
      dimensionFilters: AnalysisDimensionFilter[];
    }) => {
      if (!filterStorageKey) return;
      const diff = diffFromDefaults(applied, dashboardDefaults);
      writePersistedToStorage(filterStorageKey, diff);
    },
    [filterStorageKey, dashboardDefaults],
  );

  const dateRangeDiffersFromDefault = useMemo(() => {
    if (!dashboard) return false;
    return (
      pendingDateRange !== dashboardDefaults.dateRange ||
      (pendingDateRange === 'custom' &&
        (pendingCustomDateStart !== dashboardDefaults.customStart ||
          pendingCustomDateEnd !== dashboardDefaults.customEnd))
    );
  }, [
    dashboard,
    pendingDateRange,
    pendingCustomDateStart,
    pendingCustomDateEnd,
    dashboardDefaults,
  ]);

  const resetDateRangeToDefault = useCallback(() => {
    setPendingDateRange(dashboardDefaults.dateRange);
    setPendingCustomDateStart(dashboardDefaults.customStart);
    setPendingCustomDateEnd(dashboardDefaults.customEnd);
  }, [dashboardDefaults]);

  // Build pending date range config (used for refresh)
  const pendingDateRangeConfig = useMemo(() => {
    if (!pendingDateRange) return undefined;
    
    return {
      date_range: mapDateRangeToBackend(pendingDateRange),
      custom_date_range_start: pendingDateRange === 'custom' ? pendingCustomDateStart : undefined,
      custom_date_range_end: pendingDateRange === 'custom' ? pendingCustomDateEnd : undefined,
      user_timezone: dashboard?.date_range_config?.user_timezone || 'UTC',
      previous_period: dashboard?.date_range_config?.previous_period || false,
    };
  }, [pendingDateRange, pendingCustomDateStart, pendingCustomDateEnd, dashboard?.date_range_config?.user_timezone, dashboard?.date_range_config?.previous_period]);

  // Compute filter and date range changes from saved defaults
  const defaultValueChanges = useMemo((): DefaultValueChange[] => {
    if (!dashboard) return [];

    const savedConfig = dashboard.date_range_config;
    const savedDR = savedConfig?.date_range || 'ALL_TIME';
    const pendingDR = mapDateRangeToBackend(pendingDateRange) || 'ALL_TIME';
    const dateRangeChanged = savedDR !== pendingDR ||
      (pendingDR === 'CUSTOM' && (
        (savedConfig?.custom_date_range_start || '') !== pendingCustomDateStart ||
        (savedConfig?.custom_date_range_end || '') !== pendingCustomDateEnd
      ));

    let dateRange = null;
    if (dateRangeChanged) {
      const savedLabel = DATE_RANGE_OPTIONS.find(o => o.value === mapDateRangeFromBackend(savedDR))?.label || savedDR;
      const pendingLabel = pendingDateRange === 'custom'
        ? `Custom (${pendingCustomDateStart} to ${pendingCustomDateEnd})`
        : DATE_RANGE_OPTIONS.find(o => o.value === pendingDateRange)?.label || pendingDateRange;
      dateRange = { savedLabel, pendingLabel };
    }

    return collectDefaultValueChanges({
      components: isInDraftMode ? editingComponents : (dashboard.components || []),
      filters: isInDraftMode ? editingFilters : (dashboard.filters || []),
      pendingDimensionFilters,
      dateRange,
    });
  }, [dashboard, pendingDateRange, pendingCustomDateStart, pendingCustomDateEnd, pendingDimensionFilters, editingComponents, editingFilters, isInDraftMode]);

  const hasDefaultValueChanges = defaultValueChanges.length > 0;

  // Poll orchestration status until it reaches a terminal status, return final orchestration
  // Terminal statuses: COMPLETE, ERROR, IN_REVIEW, REJECTED, CANCELLED
  // Non-terminal statuses: QUEUED, RUNNING, NOT_BUILDING, etc.
  const pollOrchestrationStatus = async (orchestrationId: string): Promise<any> => {
    const terminalStatuses = ['COMPLETE', 'ERROR', 'IN_REVIEW', 'REJECTED', 'CANCELLED'];
    let status = 'QUEUED';
    let iteration = 0;
    const maxIterations = 120; // 4 minutes max (120 * 2 seconds)

    while (!terminalStatuses.includes(status) && iteration < maxIterations) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      
      try {
        const orchestration = await analysisService.getOrchestrationStatus(orchestrationId);
        status = orchestration.status;

        // If we've reached a terminal status, return the final orchestration
        if (terminalStatuses.includes(status)) {
          // Re-fetch orchestration to get final state with all build_executions
          const finalOrchestration = await analysisService.getOrchestrationStatus(orchestrationId);
          return finalOrchestration;
        }
      } catch (err) {
        if (iteration < maxIterations - 1 && err instanceof Error && !err.message.includes('Build failed')) {
          // Continue polling on transient errors (but not build failures)
          continue;
        }
        throw err;
      }
      iteration++;
    }

    if (iteration >= maxIterations) {
      throw new Error('Max wait time exceeded');
    }
    
    // Should not reach here, but return the last orchestration if we do
    return await analysisService.getOrchestrationStatus(orchestrationId);
  };

  // Handle date range change (updates pending state only, not applied until refresh)
  const handleDateRangeChange = (newDateRange: string, customStart?: string, customEnd?: string) => {
    setPendingDateRange(newDateRange);
    if (newDateRange === 'custom') {
      setPendingCustomDateStart(customStart || '');
      setPendingCustomDateEnd(customEnd || '');
    } else {
      setPendingCustomDateStart('');
      setPendingCustomDateEnd('');
    }
  };

  // Handle filter change from filter components (updates pending state only, not applied until refresh)
  const handleFilterChange = useCallback((componentId: string, filter: AnalysisDimensionFilter | null) => {
    const componentsToSearch = isInDraftMode ? editingComponents : (dashboard?.components || []);

    setPendingDimensionFilters((prev) => {
      // If filter is null, just remove it (we'll identify by component's dimension_id)
      if (!filter) {
        const component = componentsToSearch.find((c) => c.id === componentId);
        if (component?.filter_config?.dimension_id) {
          return prev.filter((f) => f.dimension_id !== component.filter_config!.dimension_id);
        }
        return prev;
      }

      // Add or update the filter
      // Remove any existing filter with the same dimension_id
      const withoutExisting = prev.filter((f) => f.dimension_id !== filter.dimension_id);
      return [...withoutExisting, filter];
    });
  }, [dashboard?.components, editingComponents, isInDraftMode]);

  // Handle layout change from ReactGridLayout (drag/resize)
  const handleLayoutChange = useCallback((layout: Layout) => {
    if (!isInDraftMode) return;

    let changed = false;
    setEditingComponents((draft) => {
      layout.forEach((item: LayoutItem) => {
        const component = draft.find((c) => c.id === item.i);
        if (component) {
          if (component.x !== item.x || component.y !== item.y || component.width !== item.w || component.height !== item.h) {
            changed = true;
            component.x = item.x;
            component.y = item.y;
            component.width = item.w;
            component.height = item.h;
          }
        }
      });
    });
    if (changed) {
      setHasUnsavedChanges(true);
    }
  }, [isInDraftMode, setEditingComponents]);

  // Handle opening the config sheet for a component
  // Stable identities for the props handed to the memoized panel renderer. An
  // inline arrow here would give every panel a new prop on each render and
  // defeat the memo entirely.
  // handleRefresh is a large async closure rebuilt on every render. Rather than
  // chase its dependency list through useCallback, the ref keeps one stable
  // function identity for the prop and always calls the latest version.
  const handleRefreshRef = useRef<((force?: boolean) => Promise<void>) | null>(null);
  const handleApplyFilters = useCallback(() => {
    void handleRefreshRef.current?.();
  }, []);

  const handleChartRefreshStart = useCallback(() => setIsChartRefreshing(true), []);
  const handleChartRefreshFinished = useCallback(() => setIsChartRefreshing(false), []);

  const handleOpenConfig = useCallback((component: DashboardComponent) => {
    setConfiguringComponent(component);
    setConfigSheetOpen(true);
  }, []);

  // Handle saving component configuration
  const handleSaveComponentConfig = useCallback((updatedComponent: DashboardComponent) => {
    setEditingComponents((draft) => {
      const index = draft.findIndex((c) => c.id === updatedComponent.id);
      if (index !== -1) {
        draft[index] = updatedComponent;
      }
    });
    setHasUnsavedChanges(true);
  }, [setEditingComponents]);

  // Handle deleting a component
  const handleDeleteComponent = useCallback((componentId: string) => {
    setEditingComponents((draft) => {
      const index = draft.findIndex((c) => c.id === componentId);
      if (index !== -1) {
        draft.splice(index, 1);
      }
    });
    setHasUnsavedChanges(true);
  }, [setEditingComponents]);

  // Handle adding a new component
  const handleAddComponent = useCallback(() => {
    const newComponent: DashboardComponent = {
      id: `component-${Date.now()}`,
      title: '',
      x: 0,
      y: 0,
      width: 4,
      height: 6,
      object_type: 'ANALYSIS',
    };
    setEditingComponents((draft) => {
      draft.push(newComponent);
    });
    setHasUnsavedChanges(true);
    // Open config sheet for the new component
    setConfiguringComponent(newComponent);
    setConfigSheetOpen(true);
  }, [setEditingComponents]);

  // Core save function
  // Read through a ref: executeSave is memoized on a narrow dependency list and
  // adding filter state to it would rebuild the callback on every edit.
  const editingFiltersRef = useRef<DashboardFilterOption[]>([]);
  editingFiltersRef.current = editingFilters;
  const editingThemeRef = useRef<DashboardTheme | null>(null);
  editingThemeRef.current = editingTheme;
  const editingCompactSidebarRef = useRef(false);
  editingCompactSidebarRef.current = editingCompactSidebar;
  const dashboardRef = useRef(dashboard);
  dashboardRef.current = dashboard;

  const executeSave = useCallback(async (components: DashboardComponent[], dateRangeConfig: any) => {
    if (!dashboardId) return;
    setIsSaving(true);
    try {
      await dashboardService.update(dashboardId, {
        components,
        filters: editingFiltersRef.current,
        theme: editingThemeRef.current,
        // Merged, not replaced: presentation also carries chromeless, the page
        // header flag and max width, and none of those are edited here.
        presentation: {
          ...(dashboardRef.current?.presentation || {}),
          compact_sidebar: editingCompactSidebarRef.current,
        },
        date_range_config: dateRangeConfig,
      });
      queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] });
      setHasUnsavedChanges(false);
      toastService.success('Dashboard saved successfully');
    } catch (err) {
      console.error('Failed to save dashboard:', err);
      toastService.error('Failed to save dashboard', {
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
      });
    } finally {
      setIsSaving(false);
    }
  }, [dashboardId, queryClient]);

  // Rename saves immediately and only sends `name`, so unsaved layout edits stay pending
  const handleRenameDashboard = useCallback(async (newName: string) => {
    if (!dashboardId) return;
    try {
      await dashboardService.update(dashboardId, { name: newName });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] }),
        queryClient.invalidateQueries({ queryKey: ['dashboards'] }),
      ]);
      toastService.success('Dashboard renamed');
    } catch (err) {
      console.error('Failed to rename dashboard:', err);
      toastService.error('Failed to rename dashboard', {
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
      });
    }
  }, [dashboardId, queryClient]);

  // Save without dialog (used by unsaved changes guard)
  const handleSaveDashboard = useCallback(async () => {
    if (!dashboardId || !hasUnsavedChanges) return;
    await executeSave(editingComponents, dashboard?.date_range_config);
  }, [dashboardId, editingComponents, hasUnsavedChanges, executeSave, dashboard?.date_range_config]);

  // Save click handler — shows confirmation dialog if there are default value changes
  const handleSaveClick = useCallback(() => {
    if (hasDefaultValueChanges) {
      const initialChecked = new Map<string, boolean>();
      defaultValueChanges.forEach(change => initialChecked.set(change.id, true));
      setSaveChangeChecked(initialChecked);
      setShowSaveConfirmDialog(true);
    } else {
      handleSaveDashboard();
    }
  }, [hasDefaultValueChanges, defaultValueChanges, handleSaveDashboard]);

  // Confirmed save from dialog — applies checked default value changes
  const handleConfirmedSave = useCallback(async () => {
    setShowSaveConfirmDialog(false);

    const { components: componentsToSave, filters: filtersToSave } = applyDefaultValueChanges({
      changes: defaultValueChanges,
      checked: saveChangeChecked,
      components: editingComponents,
      filters: editingFilters,
      pendingDimensionFilters,
    });

    // Determine date range to save
    const dateRangeChange = defaultValueChanges.find(c => c.type === 'date_range');
    const saveDateRange = dateRangeChange && saveChangeChecked.get('date_range')
      ? pendingDateRangeConfig
      : dashboard?.date_range_config;

    // Update editing state to reflect saved defaults
    setEditingComponents(componentsToSave);
    setEditingFilters(filtersToSave);
    // executeSave reads the filters through a ref, which React will not have
    // refreshed from the setState above by the time we await — components are
    // passed explicitly for the same reason.
    editingFiltersRef.current = filtersToSave;

    await executeSave(componentsToSave, saveDateRange);
  }, [defaultValueChanges, saveChangeChecked, editingComponents, editingFilters, pendingDimensionFilters, pendingDateRangeConfig, dashboard?.date_range_config, executeSave, setEditingComponents]);

  useUnsavedChangesGuard(hasUnsavedChanges, handleSaveDashboard);

  // Handle dashboard-wide refresh
  const handleRefresh = async (force: boolean = !supersetsEnabled) => {
    if (!dashboard) {
      return;
    }

    setIsRefreshing(true);
    setComponentErrors(new Map());
    try {
      // In draft mode use editingComponents (includes unsaved components), otherwise use saved dashboard components
      const componentsToRefresh = isInDraftMode ? editingComponents : (dashboard?.components || []);
      const savedAnalysisComponents = componentsToRefresh.filter(
        (c) => c.object_type === 'ANALYSIS' && c.object_id
      );
      // Inline components carry their definition, so there is nothing to fetch —
      // but they still need prepping, or a generated dashboard refreshes every
      // panel except its own.
      const inlineComponents = componentsToRefresh
        .filter((c) => c.object_type === 'INLINE_ANALYSIS' && c.analysis_definition);
      // The component id rides along so the server can derive the analysis id —
      // an inline definition has no document of its own, and without an id it
      // matches nothing in the skip list or the superset coverage set, which is
      // what turned one dashboard build into one build per panel.
      const inlineAnalyses = inlineComponents.map((c) => ({
        ...(c.analysis_definition as Record<string, unknown>),
        inline_component_id: c.id,
      }) as unknown as Analysis);

      if (savedAnalysisComponents.length === 0 && inlineAnalyses.length === 0) {
        toastService.info('No chart components to refresh');
        setIsRefreshing(false);
        return;
      }

      // Fetch analyses by id directly — the paginated useAnalyses cache can miss
      // analyses that fall outside the first page.
      const analysisIds = Array.from(new Set(
        savedAnalysisComponents.map((c) => c.object_id!).filter((id): id is string => !!id)
      ));
      const fetchedAnalyses = await Promise.all(
        analysisIds.map((id) => analysisService.findById(id).catch(() => null))
      );
      const analysesById = new Map<string, Analysis>();
      fetchedAnalyses.forEach((a) => {
        if (a) analysesById.set(a.id, a);
      });
      const analysesToPrep = [
        ...savedAnalysisComponents
          .map((c) => (c.object_id ? analysesById.get(c.object_id) : undefined))
          .filter((a): a is Analysis => a !== undefined),
        ...inlineAnalyses,
      ];

      if (analysesToPrep.length === 0) {
        toastService.info('No valid analyses found');
        setIsRefreshing(false);
        return;
      }

      // Get skip date range analysis IDs
      const skipDateRangeAnalyses = savedAnalysisComponents
        .filter((c) => c.ignore_date_range && c.object_id)
        .map((c) => c.object_id!)
        .filter((id): id is string => id !== undefined);

      // Same flag for inline panels, named by component id. Without this an
      // inline panel set to ignore the dashboard's date range was never in the
      // skip list, so it built against the dashboard window and stayed on
      // "not built yet" for the window it was actually asked for.
      const skipDateRangeComponents = inlineComponents
        .filter((c) => c.ignore_date_range)
        .map((c) => c.id);

      // Use the pending date range config (not saved to dashboard)
      const dateRangeConfig = pendingDateRangeConfig;

      // Use the pending dimension filters (not yet applied to charts)
      const filtersToUse = pendingDimensionFilters;

      // Call prep endpoint with dimension filters. The backend applies
      // DATASET_FRESHNESS_TOLERANCE_SECONDS as the default staleness window
      // when no explicit threshold is passed, so a fresh node is a no-op.
      const prepResult = await analysisService.prepAnalyses(
        analysesToPrep,
        filtersToUse, // Pass pending dimension filters
        dateRangeConfig,
        skipDateRangeAnalyses.length > 0 ? skipDateRangeAnalyses : undefined,
        force,
        undefined, // Let backend apply default freshness tolerance
        dashboardId || undefined, // Build dashboard-level superset
        skipDateRangeComponents.length > 0 ? skipDateRangeComponents : undefined
      );

      // Capture the analysis→node mapping from the prep response
      const analysisNodeMap = prepResult.analysis_node_map || {};

      // Poll orchestration status until complete
      const finalOrchestration = await pollOrchestrationStatus(prepResult.record.id);

      // Use model_statuses + analysis_node_map to determine per-analysis errors
      const modelStatuses = finalOrchestration.model_statuses || {};

      // Process each analysis based on its model status
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];
      const newComponentErrors = new Map<string, string>();

      // Update active date range config and dimension filters BEFORE invalidating queries
      // This ensures charts reload with the new date range and filters without flicker
      setActiveDateRangeConfig(pendingDateRangeConfig);
      setDimensionFilters(pendingDimensionFilters);

      // Persist only at the moment of apply — pending-only changes never get saved
      persistAppliedFilters({
        dateRange: pendingDateRange,
        customStart: pendingCustomDateStart,
        customEnd: pendingCustomDateEnd,
        dimensionFilters: pendingDimensionFilters,
      });

      analysesToPrep.forEach((analysis) => {
        const nodeId = analysisNodeMap[analysis.id];
        const modelStatus = nodeId ? modelStatuses[nodeId] : undefined;

        if (modelStatus && modelStatus.status.toUpperCase() === 'ERROR') {
          errorCount++;
          const errorMsg = modelStatus.error || 'Build failed';
          errors.push(`${analysis.name || analysis.id}: ${errorMsg}`);

          // Find all components that reference this analysis and set their errors.
          // An inline component has no object_id, so it is matched on the
          // definition object itself — the same one handed to prep above.
          componentsToRefresh
            .filter((c) =>
              c.object_id === analysis.id ||
              (c.object_type === 'INLINE_ANALYSIS' &&
                (c.analysis_definition as unknown as Analysis) === analysis)
            )
            .forEach((c) => {
              newComponentErrors.set(c.id, errorMsg);
            });

          // Still invalidate so the chart can attempt to show stale data or error
          queryClient.invalidateQueries({ queryKey: ['analysisData', analysis.id] });
        } else {
          // Success or no status info - invalidate queries to trigger data refresh
          queryClient.invalidateQueries({ queryKey: ['analysisData', analysis.id] });
          successCount++;
        }
      });

      setComponentErrors(newComponentErrors);

      // Show appropriate toast message
      if (errorCount === 0) {
        if (prepResult.build_skipped) {
          toastService.info('Existing dataset found. Skipping build');
        } else {
          toastService.success('Dashboard refreshed successfully');
        }
      } else if (successCount === 0) {
        toastService.error('All charts failed to refresh', {
          description: errors.join('; '),
        });
      } else {
        toastService.warning(`${successCount} chart(s) refreshed, ${errorCount} failed`, {
          description: errors.join('; '),
        });
      }
    } catch (err) {
      console.error('Failed to refresh dashboard:', err);
      toastService.error('Failed to refresh dashboard', {
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
      });
    } finally {
      setIsRefreshing(false);
    }
  };
  handleRefreshRef.current = handleRefresh;

  if (!dashboardId) {
    return (
      <div className="p-8">
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">No dashboard ID provided</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full flex flex-col bg-page-bg overflow-hidden" style={{ height: fillParent ? '100%' : 'calc(100vh)' }}>
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="w-full flex flex-col bg-page-bg overflow-hidden" style={{ height: fillParent ? '100%' : 'calc(100vh)' }}>
        <div className="p-8">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              {error ? 'Failed to load dashboard. Please try again.' : 'Dashboard not found.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pl-dashboard w-full flex flex-col bg-page-bg overflow-hidden"
      style={{
        height: fillParent ? '100%' : 'calc(100vh)',
        ...(presentation?.background_color ? { background: presentation.background_color } : {}),
        // Only the page-wide part of the theme. The surface colors go on the
        // content element below, so the top bar and the filter bar keep the
        // app's own chrome — a dashboard owns its canvas, not the frame.
        ...themeToCssVars(displayTheme, 'dashboard', 'root'),
      }}
    >
      {/* Top Bar */}
      <div className="pl-dashboard-header flex flex-col md:flex-row md:items-center justify-between w-full px-4 md:px-6 py-3 md:py-4 page-header-surface flex-shrink-0 z-10 gap-3">
        <div className="flex items-start gap-2">
          <SidebarTrigger className="md:hidden h-5 w-5" />
          <div className="flex flex-col gap-1 md:gap-2 flex-1 min-w-0">
          {/* Breadcrumbs — hidden whenever the app shell is gone: they link back
              into /analytics, which is exactly what a chromeless or embedded
              view is not offering. On mobile, hide the current page name
              (the title shows it). */}
          {!isChromeless && (
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/analytics">Analytics</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {/* No trailing crumb: the dashboard's name is the <h1> directly
                    below, and printing it twice is just noise. The crumb that
                    earns its place is the one you can click back to. */}
              </BreadcrumbList>
            </Breadcrumb>
          )}
          {/* Page Title — a white-labeled dashboard may title itself with its
              own components, in which case presentation.hide_page_header drops
              this block and keeps the viewer controls on the right.
              
              Renaming is gated on isChromeless rather than embeddedDashboardId:
              a chromeless dashboard is one someone was sent to, and a pencil on
              its title is an authoring affordance in a view that is meant to
              have none — same rule the buttons on the right follow. */}
          {!hidePageHeader && (
            <>
              {isInDraftMode && !isChromeless ? (
                <EditableTitle
                  title={dashboard.name || 'Untitled Dashboard'}
                  onSave={handleRenameDashboard}
                  entityLabel="Dashboard"
                  titleClassName="text-lg md:text-xl"
                />
              ) : (
                <h1 className="text-lg md:text-xl font-semibold text-foreground">{dashboard.name || 'Untitled Dashboard'}</h1>
              )}
              {dashboard.description && (
                <p className="text-sm text-muted-foreground">{dashboard.description}</p>
              )}
            </>
          )}
        </div>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap xl:flex-nowrap [&>button]:text-xs [&>button]:md:text-sm [&>button]:h-8 [&>button]:md:h-9 [&>button]:px-2.5 [&>button]:md:px-4">
          {/* Edit Mode Controls — authoring affordances have no place in a
              white-labeled view, regardless of the viewer's own role. */}
          {isInDraftMode && !isChromeless && (
            <>
              <Button
                variant="outline"
                onClick={handleAddComponent}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Component
              </Button>
              <Button
                variant="outline"
                onClick={() => setThemeSheetOpen(true)}
                className="flex items-center gap-2"
                title="Dashboard colors"
              >
                <Palette className="h-4 w-4" />
                Appearance
              </Button>
              <Button
                onClick={handleSaveClick}
                disabled={!(hasUnsavedChanges || hasDefaultValueChanges) || isSaving}
                className="flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(true)}
                className="text-destructive hover:text-destructive flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>


      {/* Filter bar. The top bar had become the dashboard's whole control
          surface — title, edit actions, date range and refresh — and the
          controls a viewer actually uses were mixed in with the ones only an
          editor needs. These belong together and below. */}
      <div className="pl-dashboard-filters flex items-end gap-x-4 flex-nowrap overflow-x-auto px-4 md:px-6 py-3 border-b border-border bg-page-bg flex-shrink-0">
        {visibleBarFilters.map((f) => (
          <FilterComponent
            key={f.id || f.dimension_id}
            variant="bar"
            filterConfig={f}
            componentId={f.id || `bar-${f.dimension_id}`}
            onFilterChange={handleFilterChange}
            currentFilter={pendingDimensionFilters.find((pf) => pf.dimension_id === f.dimension_id) || null}
            activeFilter={dimensionFilters.find((af) => af.dimension_id === f.dimension_id) || null}
            otherActiveFilters={pendingDimensionFilters.filter((pf) => pf.dimension_id !== f.dimension_id)}
            disabled={isRefreshing}
          />
        ))}

        {overflowBarFilters.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex-shrink-0">
                <SlidersHorizontal className="h-4 w-4 mr-1.5" />
                {overflowBarFilters.length} more
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto max-w-[520px] p-4">
              <div className="flex flex-col gap-3">
                {overflowBarFilters.map((f) => (
                  <FilterComponent
                    key={f.id || f.dimension_id}
                    variant="bar"
                    filterConfig={f}
                    componentId={f.id || `bar-${f.dimension_id}`}
                    onFilterChange={handleFilterChange}
                    currentFilter={pendingDimensionFilters.find((pf) => pf.dimension_id === f.dimension_id) || null}
                    activeFilter={dimensionFilters.find((af) => af.dimension_id === f.dimension_id) || null}
                    otherActiveFilters={pendingDimensionFilters.filter((pf) => pf.dimension_id !== f.dimension_id)}
                    disabled={isRefreshing}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {isInDraftMode && (
          <div className="flex-shrink-0">
            <AddDashboardFilter onAdd={handleAddBarFilter} />
          </div>
        )}

        {/* Date range and refresh sit at the end: they apply to everything to
            their left, and one Refresh commits every pending filter at once —
            an Apply per control was the busiest thing on the page. */}
        <div className="ml-auto flex-shrink-0 flex items-end gap-2">
          {/* Date Range Selector */}
          {/* Labelled like the filters to its left so the whole bar shares
              one label row and one control row. */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Date Range</label>
            <DateRangePicker
              value={pendingDateRange}
              customDateStart={pendingCustomDateStart}
              customDateEnd={pendingCustomDateEnd}
              onChange={handleDateRangeChange}
              disabled={isRefreshing}
              availableOptions={availableDateRangeOptions}
            />
          </div>
          {dateRangeDiffersFromDefault && (
            <Button
              variant="ghost"
              size="icon"
              onClick={resetDateRangeToDefault}
              disabled={isRefreshing}
              title="Reset date range to default"
              aria-label="Reset date range to default"
              className="!w-10 !px-0 text-muted-foreground"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          <div className="flex items-center">
            <Button
              onClick={() => handleRefresh()}
              disabled={isRefreshing || isChartRefreshing}
              className={`flex items-center gap-2 ${supersetsEnabled ? 'rounded-r-none' : ''}`}
            >
              {isRefreshing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </>
              )}
            </Button>
            {supersetsEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={isRefreshing || isChartRefreshing}
                    className="rounded-l-none border-l border-primary-foreground/20 px-2"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleRefresh(false)}>
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleRefresh(true)}>
                    <RefreshCw className="h-4 w-4" />
                    Force Refresh
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {/* overflow-x-auto, not hidden: narrow containers (a side panel dragged
          small) cannot shrink charts and tables past their own minimums, and
          hiding the overflow made that content unreachable. */}
      <div
        className="pl-dashboard-content flex-1 overflow-y-auto overflow-x-auto p-4 md:p-8 min-h-0 bg-page-bg"
        style={themeToCssVars(displayTheme, 'dashboard', 'content')}
      >
        <div
          ref={containerRef}
          className="pl-dashboard-grid"
          style={{
            width: '100%',
            // Below this the 12-column grid gives each column a couple of
            // pixels and panels render unreadably; scroll instead of squashing.
            minWidth: fillParent ? MIN_GRID_WIDTH : undefined,
            ...(presentation?.max_width
              ? { maxWidth: presentation.max_width, marginLeft: 'auto', marginRight: 'auto' }
              : {}),
          }}
        >
        {displayComponents.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">This dashboard has no components yet.</p>
            {isInDraftMode && !isChromeless && (
              <Button
                variant="outline"
                onClick={handleAddComponent}
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add your first component
              </Button>
            )}
          </div>
        ) : (
          containerMounted && isMobile ? (
            <div className="flex flex-col gap-4">
              {mobileComponents.map((component) => {
                const currentFilter = component.object_type === 'FILTER' && component.filter_config?.dimension_id
                  ? pendingDimensionFilters.find((f) => f.dimension_id === component.filter_config!.dimension_id) || null
                  : null;

                // On mobile, use a consistent height for charts rather than
                // the desktop grid height (which can be too tall or too short).
                const isChart = component.object_type !== 'FILTER' && component.object_type !== 'MARKDOWN';
                const heightPx = component.object_type === 'FILTER' ? 60
                  : component.object_type === 'MARKDOWN' ? Math.max(80, Math.min(200, component.height * 30))
                  : 360;  // charts get 360px on mobile

                return (
                  <div key={component.id} style={isChart ? { height: heightPx } : { minHeight: heightPx }}>
                    <DashboardComponentRenderer
                      component={component}
                      dateRangeConfig={activeDateRangeConfig}
                      dimensionFilters={dimensionFilters}
                      pendingDimensionFilters={pendingDimensionFilters}
                      forceShowPrepLoader={isRefreshing}
                      onFilterChange={handleFilterChange}
                      currentFilter={currentFilter}
                      disabled={isRefreshing}
                      onApplyFilters={handleApplyFilters}
                      filterApplyDisabled={isRefreshing || isChartRefreshing}
                      onChartRefresh={handleChartRefreshStart}
                      onChartRefreshFinished={handleChartRefreshFinished}
                      chartRefreshDisabled={isChartRefreshing}
                      isEditing={false}
                      onConfigure={handleOpenConfig}
                      componentError={componentErrors.get(component.id)}
                    />
                  </div>
                );
              })}
            </div>
          ) : containerMounted ? (
            <ReactGridLayout
              className="layout"
              width={containerWidth}
              gridConfig={{ cols: 12, rowHeight: 30, margin: [16, 16] }}
              dragConfig={{ enabled: isInDraftMode, handle: '.drag', cancel: '.nodrag' }}
              resizeConfig={{ enabled: isInDraftMode }}
              onLayoutChange={handleLayoutChange}
            >
              {displayComponents.map((component) => {
                const currentFilter = component.object_type === 'FILTER' && component.filter_config?.dimension_id
                  ? pendingDimensionFilters.find((f) => f.dimension_id === component.filter_config!.dimension_id) || null
                  : null;

                return (
                  <div
                    key={component.id}
                    data-grid={{
                      x: component.x,
                      y: component.y,
                      w: component.width,
                      h: component.height,
                      minW: 2,
                      minH: 2,
                    }}
                    style={{ zIndex: component.object_type === 'FILTER' ? 1 : 0 }}
                  >
                    <DashboardComponentRenderer
                      component={component}
                      dateRangeConfig={activeDateRangeConfig}
                      dimensionFilters={dimensionFilters}
                      pendingDimensionFilters={pendingDimensionFilters}
                      forceShowPrepLoader={isRefreshing}
                      onFilterChange={handleFilterChange}
                      currentFilter={currentFilter}
                      disabled={isRefreshing}
                      onApplyFilters={handleApplyFilters}
                      filterApplyDisabled={isRefreshing || isChartRefreshing}
                      onChartRefresh={handleChartRefreshStart}
                      onChartRefreshFinished={handleChartRefreshFinished}
                      chartRefreshDisabled={isChartRefreshing}
                      isEditing={isInDraftMode}
                      onConfigure={handleOpenConfig}
                      componentError={componentErrors.get(component.id)}
                    />
                  </div>
                );
              })}
            </ReactGridLayout>
          ) : null
        )}
        </div>

        {/* Component Config Sheet */}
        <ComponentConfigSheet
          component={configuringComponent}
          open={configSheetOpen}
          onOpenChange={setConfigSheetOpen}
          onSave={handleSaveComponentConfig}
          onDelete={handleDeleteComponent}
          dashboardTheme={displayTheme}
        />

        {/* Dashboard colors. Its own sheet rather than a panel setting: it is
            the tier every panel inherits from, and it has nowhere else to live
            now that the top bar is deliberately thin. */}
        <Sheet open={themeSheetOpen} onOpenChange={setThemeSheetOpen}>
          <SheetContent className="sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Appearance</SheetTitle>
              <SheetDescription>
                Colors for this dashboard. Anything left inherited follows your
                organization's branding, and any panel can override these again.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="compact-sidebar">Compact sidebar</Label>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Open with the app's navigation collapsed to icons. For a
                    dashboard you send people to directly — they can still
                    expand it, and still only see what their role allows.
                  </p>
                </div>
                <Switch
                  id="compact-sidebar"
                  checked={editingCompactSidebar}
                  onCheckedChange={(checked) => {
                    setEditingCompactSidebar(checked);
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>
              <ThemeEditor
                theme={editingTheme}
                onChange={(next) => {
                  setEditingTheme(next);
                  setHasUnsavedChanges(true);
                }}
                scope="dashboard"
                inherited={inheritedDashboardColors}
              />
            </div>
          </SheetContent>
        </Sheet>

        {/* Save Default Values Confirmation */}
        <AlertDialog open={showSaveConfirmDialog} onOpenChange={setShowSaveConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Update Default Values</AlertDialogTitle>
              <AlertDialogDescription>
                The following filter and date range values have changed from the saved defaults.
                Uncheck any you'd like to keep as-is.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4 space-y-3">
              {defaultValueChanges.map(change => (
                <div key={change.id} className="flex items-start gap-3">
                  <Checkbox
                    id={`save-change-${change.id}`}
                    checked={saveChangeChecked.get(change.id) ?? true}
                    onCheckedChange={(checked) => {
                      setSaveChangeChecked(prev => {
                        const next = new Map(prev);
                        next.set(change.id, !!checked);
                        return next;
                      });
                    }}
                  />
                  <Label htmlFor={`save-change-${change.id}`} className="flex flex-col gap-0.5 cursor-pointer">
                    <span className="text-sm font-medium">{change.label}</span>
                    <span className="text-xs text-muted-foreground">{change.description}</span>
                  </Label>
                </div>
              ))}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={isSaving}
                onClick={async (e) => {
                  e.preventDefault();
                  await handleConfirmedSave();
                }}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Dashboard Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Dashboard</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{dashboard.name || 'Untitled Dashboard'}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={async (e) => {
                  e.preventDefault();
                  await handleDeleteDashboard();
                }}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

